/**
 * Services Library — org-wide catalogue of reusable interior
 * design SERVICES (Brand Identity Design, 3D Walkthrough,
 * Site Supervision, etc.). The quotation Step 2 "From Library"
 * picker pulls from this collection.
 *
 * Distinct from `materialLibrary` — materials are physical
 * goods (plywood, paint, hardware) used by `materialRequests`
 * on projects. Services are the studio's billable expertise
 * lines that appear on quotes / invoices. Same studio admin
 * curates both, but they serve different surfaces.
 *
 * Schema: `serviceLibrary/{serviceId}` — org-scoped.
 * Mirrors the materialLibrary write pattern (addDoc + updateDoc
 * + deleteDoc, serverTimestamp on create, deleteField for
 * cleared optional numbers).
 */
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

// ───── Categories ─────────────────────────────────────────────

export type ServiceCategory =
  | 'design'             // Brand identity, mood boards, palettes
  | 'drawing'            // AutoCAD, technical drawings, layouts
  | 'visualization'      // 3D renders, walkthroughs, VR
  | 'execution'          // On-site build, supervision, install
  | 'consulting'         // Site visits, advisory, second opinions
  | 'project_management' // Coordination, vendor management
  | 'styling'            // Soft furnishing, staging
  | 'other';

export type ServiceCategoryConfig = {
  key: ServiceCategory;
  label: string;
  icon: string;       // Phosphor icon class
  color: string;      // Accent hex (pill text)
  tintBg: string;     // Pill background
  defaultUnit: string;
};

/** The 8 service buckets a typical Indian interior design studio
 *  bills against. Each has a default unit so the form can
 *  pre-fill sensibly (Design is per-Nos / per-project, Execution
 *  is per-sqft, Consulting is per-hour, etc.). */
export const SERVICE_CATEGORIES: ServiceCategoryConfig[] = [
  {
    key: 'design',
    label: 'Design',
    icon: 'ph ph-palette',
    color: '#7C3AED',
    tintBg: '#F5F3FF',
    defaultUnit: 'Nos',
  },
  {
    key: 'drawing',
    label: 'Drawing',
    icon: 'ph ph-compass-tool',
    color: '#0EA5E9',
    tintBg: '#E0F2FE',
    defaultUnit: 'Nos',
  },
  {
    key: 'visualization',
    label: '3D / Visualization',
    icon: 'ph ph-cube',
    color: '#DB2777',
    tintBg: '#FCE7F3',
    defaultUnit: 'Nos',
  },
  {
    key: 'execution',
    label: 'Execution',
    icon: 'ph ph-hammer',
    color: '#D97706',
    tintBg: '#FEF3C7',
    defaultUnit: 'Sq.ft',
  },
  {
    key: 'consulting',
    label: 'Consulting',
    icon: 'ph ph-chat-circle-text',
    color: '#059669',
    tintBg: '#D1FAE5',
    defaultUnit: 'Hrs',
  },
  {
    key: 'project_management',
    label: 'Project Management',
    icon: 'ph ph-clipboard-text',
    color: '#2563EB',
    tintBg: '#DBEAFE',
    defaultUnit: 'L.S.',
  },
  {
    key: 'styling',
    label: 'Styling',
    icon: 'ph ph-sparkle',
    color: '#DC2626',
    tintBg: '#FEE2E2',
    defaultUnit: 'Day',
  },
  {
    key: 'other',
    label: 'Other',
    icon: 'ph ph-dots-three',
    color: '#6B7280',
    tintBg: '#F3F4F6',
    defaultUnit: 'Nos',
  },
];

export function getServiceCategoryConfig(
  key: ServiceCategory | string,
): ServiceCategoryConfig {
  return (
    SERVICE_CATEGORIES.find((c) => c.key === key) ??
    SERVICE_CATEGORIES[SERVICE_CATEGORIES.length - 1]
  );
}

// ───── Item shape ─────────────────────────────────────────────

export type ServiceLibraryItem = {
  id: string;
  orgId: string;
  category: ServiceCategory;
  /** Short, customer-facing title — appears on the quote line. */
  name: string;
  /** Optional descriptive blurb. Auto-fills the quote line's
   *  description field on pick. Studios use this to lock down
   *  scope wording so every quote uses the same language. */
  description?: string;
  /** Default billing unit from `QUOTATION_UNITS`. */
  unit: string;
  /** Default rate per unit. Optional — studios can leave blank
   *  if pricing is always per-quote. */
  defaultRate?: number;
  /** HSN / SAC code for GST. Services use SAC (998xxx). */
  hsn?: string;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
};

// ───── Writes ────────────────────────────────────────────────

export type CreateServiceInput = {
  orgId: string;
  category: ServiceCategory;
  name: string;
  description?: string;
  unit: string;
  defaultRate?: number;
  hsn?: string;
  createdBy: string;
};

export async function createService(
  input: CreateServiceInput,
): Promise<string> {
  const payload: Record<string, unknown> = {
    orgId: input.orgId,
    category: input.category,
    name: input.name.trim(),
    unit: input.unit,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
  };
  const desc = input.description?.trim();
  if (desc) payload.description = desc;
  const hsn = input.hsn?.trim();
  if (hsn) payload.hsn = hsn;
  if (input.defaultRate != null && input.defaultRate > 0) {
    payload.defaultRate = input.defaultRate;
  }
  const ref = await addDoc(collection(db, 'serviceLibrary'), payload);
  return ref.id;
}

export async function updateService(
  id: string,
  data: Partial<Omit<CreateServiceInput, 'orgId' | 'createdBy'>>,
): Promise<void> {
  const updates: Record<string, unknown> = {};
  if (data.category !== undefined) updates.category = data.category;
  if (data.name !== undefined) updates.name = data.name.trim();
  if (data.unit !== undefined) updates.unit = data.unit;
  if (data.description !== undefined) {
    const d = data.description.trim();
    updates.description = d || deleteField();
  }
  if (data.hsn !== undefined) {
    const h = data.hsn.trim();
    updates.hsn = h || deleteField();
  }
  if (data.defaultRate !== undefined) {
    updates.defaultRate =
      data.defaultRate > 0 ? data.defaultRate : deleteField();
  }
  await updateDoc(doc(db, 'serviceLibrary', id), updates);
}

export async function deleteService(id: string): Promise<void> {
  await deleteDoc(doc(db, 'serviceLibrary', id));
}
